import React, { useState } from 'react';
import SEO from '../seo/SEO';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { pathFor } from '../routes/paths';
import { useLanguage } from '../contexts/LanguageContext';

const ACCENT = '#67313a';

const PageWrap = styled.div`
  background: #fbf7f2;
`;

const Hero = styled.section`
  padding: 150px 0 60px;
  text-align: center;
  background: #fbf7f2;
  @media (max-width: 600px) {
    padding: 90px 24px 60px;
  }
`;

const BigTitle = styled(motion.h1)`
  font-family: 'Tilt Warp', cursive;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${ACCENT};
  font-size: clamp(24px, 4.2vw, 46px);
  margin-bottom: 15px;
  margin-top: 15px;
  padding: 20px 0px;
`;

const Collage = styled.div`
  position: relative;
  max-width: 1080px;
  height: 520px;
  margin: 0 auto 20px;

  img {
    position: absolute;
    display: block;
    box-shadow: 0 18px 40px rgba(0,0,0,0.14);
    border-radius: 2px;
    object-fit: cover;
  }

  img.center {
    width: 280px; /* biggest (scaled down further) */
    height: 437px;
    top: 50px; /* centered vertically */
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
  }

  img.left {
    width: 184px; /* smallest (scaled down further) */
    height: 292px;
    left: 210px; /* positioned to overlap with center */
    top: 130px; /* positioned lower to create layering effect */
    z-index: 2;
  }

  img.right {
    width: 184px; /* medium-small (scaled down further) */
    height: 292px;
    right: 210px; /* positioned to overlap with center */
    top: 130px; /* positioned lower and slightly offset from left */
    z-index: 1;
  }

  @media (max-width: 968px) {
    height: 400px;
    img.center { width: 240px; height: 375px; top: 10px; }
    img.left { width: 158px; height: 251px; left: 20px; top: 80px; }
    img.right { width: 158px; height: 251px; right: 20px; top: 80px; }
  }
`;

const Label = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #a28f86;
  margin-top: 12px;
`;

const BigSubTitle = styled.h2`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  text-transform: uppercase;
  color: ${ACCENT};
  font-size: clamp(22px, 4vw, 36px);
  margin: 25px auto;
`;

const IntroText = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-weight: 300;
  text-align: center;
  font-size: 16px;
  line-height: 1.9;
  color: #6f6a68;
  max-width: 75%;
  margin: 20px auto 10px;
`;

const Divider = styled.div`
  height: 1px;
  background: #e6ded9;
  max-width: 1000px;
  margin: 28px auto 0;
`;

const Priorities = styled.section`
  background: url('/images/Marbre 1 - 1500px.webp') center/cover no-repeat;
  color: #ffffff;
  text-align: center;
  padding: 80px 0;
  @media (max-width: 600px) {
    padding: 80px 24px;
  }
`;

const Mono = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
  opacity: 0.9;
  margin-bottom: 18px;
`;

const PrioritiesTitle = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-size: clamp(18px, 3vw, 26px);
`;

/* ===== Values 01..04 ===== */
const ValuesSection = styled.section`
  background: #f6efe8;
  padding: 90px 0 70px;
`;

const ValuesGrid = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px 120px;
  padding: 0 80px;

  @media (max-width: 1100px) {
    padding: 0 40px;
    gap: 48px 70px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ValueCard = styled.div``;

const ValueNumber = styled.div`
  font-family: 'Tiro Bangla', serif;
  color: #67313a;
  font-size: 38px;
  letter-spacing: 2px;
  margin-bottom: 6px;
`;

const ValueHeading = styled.h4`
  font-family: 'Tiro Bangla', serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #6b6b6b;
  margin: 0;
  font-size: 18px;
`;

const ValueRule = styled.div`
  width: 64px;
  height: 2px;
  background: #e6ded9;
  margin: 12px 0 14px;
`;

const ValueText = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 15px;
  line-height: 1.85;
  color: #6f6a68;
  max-width: 520px;

  b { color: #2f2f2f; font-weight: 600; }
`;

/* ===== Testimonial Strip ===== */
const TestimonialStrip = styled.section`
  background: linear-gradient(rgba(0, 0, 0, 0.54), rgba(0, 0, 0, 0.76)), url('/images/banniere-testimonial-wedding-experience.webp') center/cover no-repeat;
  padding: 190px 0;
  text-align: center;
  @media (max-width: 600px) {
    padding: 80px 24px;
  }
`;

const Quote = styled.blockquote`
  max-width: 880px;
  margin: 0 auto;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: clamp(18px, 2.4vw, 24px);
  line-height: 1.3;
  color:rgb(249, 249, 249);
`;

const QuoteAuthor = styled.div`
  margin-top: 18px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color:rgb(243, 240, 239);
`;

/* ===== Distinctions Row ===== */
const Distinctions = styled.section`
  background: #f6efe8;
  padding: 30px 0 60px;
`;

const DistBar = styled.div`
  width: 31%;
  height: 2px;
  background: #e0d8d2;
  margin: 25px auto 0;
  opacity: 0.9;

  @media (max-width: 900px) {
    width: 82%;
  }
`;

const DistRow = styled.div`
  display: flex;
  gap: 60px;
  justify-content: center;
  align-items: center;
  padding-top: 22px;
  flex-wrap: wrap;
`;

const DistBadge = styled.img`
  height: 40px;
  opacity: 0.9;
  filter: grayscale(30%);
`;

/* ===== Latest Weddings ===== */
const LatestWrap = styled.section`
  padding: 90px 0;
  background: #FCFAF6;
  text-align: center;
`;

const SmallLabel = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  color: #9fb3c8;
  margin-bottom: 6px;
`;

const LatestTitle = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(22px, 4vw, 30px);
  color: #2c2c2c;
  margin-bottom: 36px;
`;

const LatestGrid = styled.div`
  max-width: 1150px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  padding: 0 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const WeddingCard = styled.div`
  text-align: center;
`;

const WeddingImg = styled.img`
  width: 100%;
  height: 480px;
  object-fit: cover;
  box-shadow: 0 15px 35px rgba(0,0,0,0.08);
`;

const WeddingCaption = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #6a6a6a;
  margin-top: 16px;
`;

const CTAButton = styled(motion.button)`
  margin: 34px auto 0;
  display: inline-block;
  background: #67313a;
  border: none;
  padding: 12px 28px;
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
`;

/* ===== Storytelling Experience (inserted section) ===== */
const StoryWrap = styled.section`
  padding: 80px 0 90px;
  position: relative;
  overflow: hidden;
  text-align: center;
  background: #000; /* fallback while video loads */
  @media (max-width: 600px) {
    padding: 80px 24px 90px;
  }
`;

const StoryTitle = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #ffffff;
  font-size: clamp(22px, 3.6vw, 30px);
  margin-bottom: 14px;
`;

const StoryText = styled.p`
  max-width: 920px;
  margin: 0 auto 8px;
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 15px;
  line-height: 1.85;
  color: #f2f2f2;
`;

const StoryButton = styled(motion.button)`
  margin-top: 18px;
  background: ${ACCENT};
  border: none;
  padding: 10px 22px;
  color: #ffffff;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 2px;
`;

/* Background video layers for Story section */
const StoryVideoLayer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
`;

const StoryVideo = styled.iframe`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 177.78vh; /* cover by height on tall screens */
  height: 100%;
  @media (min-aspect-ratio: 16/9) {
    width: 100%;   /* cover by width on wide screens */
    height: 56.25vw;
  }
  border: 0;
  background: #000;
  pointer-events: none;
`;

const StoryOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.45));
  z-index: 1;
`;

const StoryInner = styled.div`
  position: relative;
  z-index: 2;
`;

/* ===== Process & Investment ===== */
const ProcessWrap = styled.section`
  padding: 80px 0;
  background: #ffffff;
`;

const ProcessHeader = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  text-transform: uppercase;
  color: #4e1f28;
  text-align: left;
  max-width: 1200px;
  margin: 0 auto 24px;
  padding: 0 40px;
`;

const ProcessGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 40px;
  padding: 0 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Hint = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  color: #9a9a9a;
  margin-bottom: 12px;
`;

const Accordion = styled.div``;

const Item = styled.div`
  border: 1px solid #e6e0db;
  margin-bottom: 10px;
`;

const ItemHeader = styled.button`
  width: 100%;
  text-align: left;
  background: #ffffff;
  border: none;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-family: 'Tiro Bangla', serif;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #6b6b6b;
`;

const Icon = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 18px;
  color: #b8a398;
`;

const ItemBody = styled.div`
  padding: 0 10px 14px;
  font-family: 'Barlow Semi Condensed', sans-serif;
  color: #6a6a6a;
  font-size: 15px;
  line-height: 1.75;
`;

const ProcessImage = styled.img`
  width: 100%;
  height: 90%;
  object-fit: cover;
  box-shadow: 0 15px 35px rgba(0,0,0,0.08);
`;

const Investment = styled.section`
  background: url('/images/MArbre Blanc - 1500 px.webp') center/cover no-repeat;
  padding: 130px 0; /* vertical spacing like mock */
`;

const InvestGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 430px 1fr; /* smaller image column */
  gap: 40px; /* a bit more white space between image and text */
  padding: 0 30px;
  align-items: start;

  @media (max-width: 1060px) {
    grid-template-columns: 1fr;
  }
`;

const InvestImage = styled.img`
  width: 85%;
  margin-left: 20%;
  height: 530px;
  object-fit: cover;
  box-shadow: 0 15px 35px rgba(0,0,0,0.12);
  display: block;

  @media (max-width: 1060px) {
    width: 92%;
    margin: 0 auto 20px;
    height: auto;
  }
`;

const InvestText = styled.div`
  align-self: start;
  display: grid;
  justify-items: center; /* center children horizontally within the column */
  margin-top: 30px; /* push the whole text block downward */

  @media (max-width: 1060px) {
      padding: 0 25px;
  }
`;

const InvestTitle = styled.h3`
  font-family: 'Tiro Bangla', serif;
  font-weight: 400;
  color: #6b6b6b;
  font-size: clamp(32px, 5.2vw, 50px);
  letter-spacing: 0.5px;
  text-align: center; /* centered in its column */
  margin-bottom: 18px;
`;

const InvestP = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 16px;
  line-height: 1.9;
  color: #5c5a59;
  max-width: 520px;
  text-align: center; /* justified text under the title */
  text-justify: inter-word;
  margin-left: auto;
  margin-right: auto; /* center the paragraph block */
  margin-bottom: 12px;
  margin-top: 20px;
`;

const InvestButton = styled(motion.button)`
  margin: 18px auto 0; /* centered button */
  background: #67313a;;
  border: 1px solid #c6c6c6;
  border-radius: 2px;
  color:rgb(255, 255, 255);
  padding: 12px 32px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
`;

const ExperiencePage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const steps = lang === 'fr'
    ? [
        {
          title: '01 Prendre contact',
          body:
            "Votre parcours commence par un premier message. Partagez un maximum de détails sur votre célébration afin que je comprenne votre vision et vous guide dès le départ, avec des réponses claires et personnalisées.",
        },
        {
          title: "02 Préparer l'exceptionnel",
          body:
            "Votre univers et votre vision guident nos premiers échanges. Nous avançons en parfaite coordination pour affiner chaque détail et garantir les conditions optimales à la création d’images d’exception.",
        },
        {
          title: "03 Saisir l'intemporel",
          body:
            "Vivez chaque instant avec intensité, entourés de ceux qui vous sont chers. En toute discrétion, je révèle l’essence de votre journée : les émotions vraies, les gestes sincères, la beauté d’un moment suspendu.",
        },
        {
          title: "04 Sublimer l'émotion",
          body:
            "Chaque image est retouchée avec précision pour en révéler toute la force, avant d’être mise en valeur sur des supports nobles et durables où chaque création prolonge l’émotion avec raffinement et justesse.",
        },
        {
          title: "05 Transmettre l’essentiel",
          body:
            "Vos photos sont livrées dans une galerie en ligne élégante et sécurisée, accessible à tout moment. Albums d’exception, tirages d’art, encadrements sur mesure… chaque support révèle l’intensité avec élégance et précision.",
        },
      ]
    : [
        {
          title: '01 Reaching Out',
          body:
            "Your journey begins with a first message. Share as many details as possible about your celebration so I can understand your vision and offer guidance from the very start. Every answer you receive is thoughtful and tailored, ensuring clarity and support from day one.",
        },
        {
          title: '02 Preparing the Exceptional',
          body:
            "From the first exchange, I collaborate with you and your wedding planner to refine every detail, ensuring the ideal conditions for rare and distinguished images.",
        },
        {
          title: '03 Capturing the Timeless',
          body:
            "On your wedding day, you are free to live each moment fully. With discretion and sensitivity, I capture the true essence of your celebration with authenticity and elegance.",
        },
        {
          title: '04 Elevating the Emotional',
          body:
            "Each photograph is refined with precision, then enhanced through heirloom albums, fine art prints, and bespoke frames, crafted to preserve your story with sophistication.",
        },
        {
          title: '05 Preserving the Essential',
          body:
            "Your images are delivered in a secure, elegant gallery that is easy to share with your loved ones. Every support, from albums to wall art, reveals your memories with timeless refinement",
        },
      ];

  return (
    <PageWrap>
      <SEO
        title={lang === 'fr' ? 'Expérience Mariage — Lino Ludovic' : 'Wedding Experience — Lino Ludovic'}
        description={
          lang === 'fr'
            ? "Approche, priorités, processus et investissement : une expérience élégante et fluide pour votre mariage."
            : 'Approach, priorities, process and investment — an elegant, seamless wedding experience.'
        }
        image="/images/DISCOVER THE GALLERIES.jpg"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: lang === 'fr' ? 'Expérience Mariage' : 'Wedding Experience'
        }}
      />
      <Hero>
        <BigTitle
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {lang === 'fr' ? "L'art de vivre votre histoire" : 'The art of living your story'}
        </BigTitle>

        {/* Collage layered like the mock */}
        <Collage>
          <img className="left" src="/images/Wedding photography experience left.webp" alt="Left board" />
          <img className="center" src="/images/Wedding photography experience center.webp" alt="Center couple" />
          <img className="right" src="/images/Wedding photography experience right.webp" alt="Right board" />
        </Collage>

        <BigSubTitle>
          {lang === 'fr'
            ? <>où chaque instant devient <br/> intemporel, avec fluidité.</>
            : <>where each moment becomes <br/> effortlessly timeless.</>}
        </BigSubTitle>

        <IntroText>
          {lang === 'fr'
            ? "Un mariage est bien plus qu’une célébration : c’est une histoire vivante. Mon rôle est d’orchestrer une expérience photographique à la fois fluide, raffinée et profondément personnelle. Qu’elle s’étende sur un ou plusieurs jours, en France ou à l’étranger, tout est pensé pour que vous viviez chaque instant pleinement, dans la confiance, pendant que je compose une narration visuelle intemporelle."
            : 'Every wedding is more than a celebration, it is a living story. My role is to craft a photography experience that feels seamless, refined, and deeply personal. Whether it unfolds over one day or several, in France or abroad, everything is designed so you may live each moment fully, with trust and ease, while I compose a timeless visual narrative.'}
        </IntroText>
        <IntroText>
          {lang === 'fr'
            ? "Mon travail est porté par un équilibre rare entre sensibilité et élégance éditoriale. Je privilégie la lumière naturelle, les gestes spontanés et ces liens invisibles qui vous unissent, en y insufflant une esthétique raffinée. Ma présence, discrète et attentive, vous permet de rester pleinement dans votre journée, avec la certitude que chaque détail est saisi avec précision et grâce."
            : 'My work is defined by a rare balance between sensitivity and editorial elegance. I favor natural light, spontaneous gestures, and the invisible bonds that connect you, while infusing them with a refined aesthetic. My presence is discreet yet attentive, allowing you to remain fully immersed in your celebration, knowing every detail is captured with precision and grace.'}
        </IntroText>

      </Hero>

      <Priorities>
        <Mono src="/images/Monogramme LL2 Blanc.webp" alt="LL Monogram" />
        <PrioritiesTitle>{lang === 'fr' ? 'Mes priorités pour votre mariage' : 'My Priorities For Your Wedding'}</PrioritiesTitle>
      </Priorities>

      {/* Values 01..04 */}
      <ValuesSection>
        <ValuesGrid>
          <ValueCard>
            <ValueNumber>01</ValueNumber>
            <ValueHeading>{lang === 'fr' ? 'ÉMOTION' : 'EMOTION'}</ValueHeading>
            <ValueRule />
            <ValueText>
              {lang === 'fr'
                ? "Au cœur de mon travail se trouve la recherche de la vraie émotion. La beauté réside dans ce qui circule entre vous : un regard furtif, un éclat de rire, l’intensité silencieuse d’un geste. Mon rôle est de révéler ces instants avec raffinement, pour en faire des images d’une profondeur rare et d’une résonance intemporelle."
                : 'At the heart of my work lies the pursuit of true emotion. Beauty lives in what flows between you: a fleeting glance, a burst of laughter, the quiet intensity of a gesture. My role is to reveal these instants with refinement, so they become images of rare depth and timeless resonance.'}
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueNumber>02</ValueNumber>
            <ValueHeading>{lang === 'fr' ? 'ÉLÉGANCE' : 'ELEGANCE'}</ValueHeading>
            <ValueRule />
            <ValueText>
              {lang === 'fr'
                ? "Chaque photographie est façonnée par une sensibilité éditoriale où la lumière, la composition et la spontanéité s’harmonisent naturellement. Inspiré par l’art de vivre à la française, je recherche un équilibre subtil entre sophistication et authenticité, pour des images lumineuses et naturelles, érigées en véritables œuvres d’art."
                : 'Each photograph is crafted with an editorial sensibility, where light, composition, and spontaneity merge seamlessly. Inspired by the French art of living, I seek a delicate balance between sophistication and authenticity, creating luminous and natural imagery that transcends into true works of art.'}
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueNumber>03</ValueNumber>
            <ValueHeading>{lang === 'fr' ? 'EXCLUSIVITÉ' : 'EXCLUSIVITY'}</ValueHeading>
            <ValueRule />
            <ValueText>
              {lang === 'fr'
                ? "Chaque célébration est unique et mérite une implication totale. Je limite volontairement le nombre de mariages que j’accepte chaque année, afin de consacrer le temps, l’énergie et l’attention nécessaires à une expérience sur‑mesure et sans friction. Cette exclusivité garantit que rien n’est laissé au hasard."
                : 'Every celebration is unique and deserves full dedication. I deliberately limit the number of weddings I accept each year, so I can devote the time, energy, and attention necessary to offer you a seamless and bespoke experience. This exclusivity ensures that nothing is left to chance.'}
            </ValueText>
          </ValueCard>
          <ValueCard>
            <ValueNumber>04</ValueNumber>
            <ValueHeading>{lang === 'fr' ? 'FIABILITÉ' : 'RELIABILITY'}</ValueHeading>
            <ValueRule />
            <ValueText>
              {lang === 'fr'
                ? "De la préparation à la livraison, chaque étape est abordée avec soin et maîtrise. En étroite collaboration avec votre wedding planner, j’anticipe la lumière, le rythme et l’atmosphère pour que vous restiez pleinement présents. Cette fiabilité vous assure de vivre votre journée en toute sérénité, avec la certitude que chaque souvenir est capturé avec intention."
                : 'From preparation to delivery, every step is approached with care and mastery. In close collaboration with your wedding planner, I anticipate light, rhythm, and atmosphere so you can remain fully present. This reliability ensures that you live your day with absolute serenity, knowing that every memory is captured with care and intention.'}
            </ValueText>
          </ValueCard>
        </ValuesGrid>
      </ValuesSection>

      {/* Testimonial Strip */}
      <TestimonialStrip>
        <Quote>
          {lang === 'fr'
            ? "« Nous avons choisi Ludovic pour nous accompagner lors de notre mariage civil et religieux. Il a su capturer les plus beaux moments de nos deux journées. À l’écoute de nos envies, il met à l’aise devant l’objectif.»"
            : '“We chose Ludovic to accompany us for both our civil and religious weddings. He captured the most beautiful moments of our two days. Attentive to our wishes, he makes you feel comfortable in front of the lens.”'}
        </Quote>
        <QuoteAuthor>{lang === 'fr' ? 'Aurélie' : 'Aurélie'}</QuoteAuthor>
      </TestimonialStrip>

      {/* Distinctions Row */}
      <Distinctions>
        <DistRow>
          <DistBadge src="/images/QUEEN FOR A DAY.gif" alt="Queen For A Day" />
          <DistBadge src="/images/MELLE DENTELLE.webp" alt="Melle Dentelle" />
          <DistBadge src="/images/LME.webp" alt="LME" />
          <DistBadge src="/images/UN BEAU JOUR.webp" alt="Un Beau Jour" />
        </DistRow>
        <DistBar />
      </Distinctions>

      {/* Process & Investment */}
      <ProcessWrap>
        <ProcessHeader>{lang === 'fr' ? 'Du premier échange à l’héritage' : 'The Process'}</ProcessHeader>
        <ProcessGrid>
          <div>
            <Hint>{lang === 'fr' ? 'Cliquez sur les titres pour en savoir plus !' : 'Click on the titles to see more!'}</Hint>
            <Accordion>
              {steps.map((s, i) => (
                <Item key={i}>
                  <ItemHeader onClick={() => setOpenIndex(openIndex === i ? -1 : i)}>
                    {s.title}
                    <Icon>{openIndex === i ? '−' : '+'}</Icon>
                  </ItemHeader>
                  {openIndex === i && (
                    <ItemBody>{s.body}</ItemBody>
                  )}
                </Item>
              ))}
            </Accordion>
          </div>
          <div>
            <ProcessImage src="/images/DISCOVER THE GALLERIES.webp" alt="wedding photographer process" />
          </div>
        </ProcessGrid>
      </ProcessWrap>

      <Investment>
        <InvestGrid>
          <div>
            <InvestImage src="/images/Adélie_et_Julien/00399- Adelie & Julien - MD.webp" alt="Couple by the lake" />
          </div>
          <InvestText>
            <InvestTitle>{lang === 'fr' ? 'Investissement' : 'Investment'}</InvestTitle>
            <InvestP>
              {lang === 'fr'
                ? "Chaque célébration mérite une attention singulière. Qu’il se déroule sur un ou plusieurs jours, votre mariage est accompagné avec discrétion et exigence, afin que chaque chapitre de votre histoire soit capturé avec harmonie et sophistication."
                : 'Every celebration deserves singular attention. Whether your wedding unfolds over one day or several, I accompany you with discretion and exacting care, ensuring each chapter of your story is captured with harmony and sophistication.'}
            </InvestP>
            <InvestP>
              {lang === 'fr'
                ? "La plupart des couples investissent entre 3 600 € et 6 200 € pour une expérience photographique complète, enrichie de supports d’exception réalisés avec soin."
                : 'Most couples invest between €3,600 and €6,200 for a complete photographic experience, enriched with exceptional, carefully crafted deliverables.'}
            </InvestP>
            <InvestButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate(pathFor('contact', lang))}>
              {lang === 'fr' ? 'Demander une offre' : 'Inquire'}
            </InvestButton>
          </InvestText>
        </InvestGrid>
      </Investment>

      {/* Storytelling Experience (inserted) */}
      <StoryWrap>
        <StoryVideoLayer>
          <StoryVideo
            src="https://www.youtube.com/embed/SWp2-jYenAw?si=y8opAztCs19VudQH&autoplay=1&mute=1&controls=0&loop=1&playsinline=1&rel=0&modestbranding=1&playlist=SWp2-jYenAw"
            title={lang === 'fr' ? 'Film de mariage — Storytelling' : 'Wedding film — Storytelling'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </StoryVideoLayer>
        <StoryOverlay />
        <StoryInner>
          <StoryTitle>{lang === 'fr' ? "L'EXPÉRIENCE STORYTELLING" : 'THE STORYTELLING EXPERIENCE'}</StoryTitle>
          <StoryText>
          {lang === 'fr'
            ? "Du dîner de bienvenue au brunch, en passant par une séance engagement ou day‑after, chaque moment s’intègre à une narration continue et élégante."
            : 'From a welcome dinner to a brunch, or through an engagement or day-after session, every moment becomes part of a continuous and elegant narrative.'}
          </StoryText>
          <StoryText>
          {lang === 'fr'
            ? "Selon votre célébration, je peux être accompagné d’un second photographe ou d’un assistant afin que chaque perspective soit couverte avec la même discrétion et harmonie. Je collabore également avec un vidéaste de confiance pour une vision fluide et alignée."
            : 'Depending on your celebration, I may be accompanied by a second photographer or an assistant, ensuring every perspective is covered with the same discretion and harmony. I also collaborate with a trusted videographer, offering you a fluid, aligned dual perspective for a complete storytelling experience.'}
          </StoryText>
          <StoryButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate(pathFor('videographer', lang))}>
            {lang === 'fr' ? "Découvrir l’expérience complète" : 'Explore the Full Experience'}
          </StoryButton>
        </StoryInner>
      </StoryWrap>

      {/* Latest Weddings */}
      <LatestWrap>
        <SmallLabel>{lang === 'fr' ? 'Découvrir les' : 'Discover the'}</SmallLabel>
        <LatestTitle>{lang === 'fr' ? 'Derniers Mariages' : 'Latest Weddings'}</LatestTitle>
        <LatestGrid>
          <WeddingCard onClick={() => navigate(pathFor('gallery', lang, { slug: 'n3' }))} style={{ cursor: 'pointer' }}>
            <WeddingImg src="/images/wedding Palace de Menthon - Savoie (2).webp" alt="Menthon-Saint-Bernard - Savoie" />
            <WeddingCaption>Menthon-Saint-Bernard - SAVOIE</WeddingCaption>
          </WeddingCard>
          <WeddingCard onClick={() => navigate(pathFor('gallery', lang, { slug: 'n2' }))} style={{ cursor: 'pointer' }}>
            <WeddingImg src="/images/Wedding entrevaux Provence.webp" alt="Entrevaux - Provence" />
            <WeddingCaption>Entrevaux - PROVENCE</WeddingCaption>
          </WeddingCard>
          <WeddingCard onClick={() => navigate(pathFor('gallery', lang, { slug: 'n1' }))} style={{ cursor: 'pointer' }}>
            <WeddingImg src="/images/Wedding Annecy Savoie.webp" alt="Annecy - Savoie" />
            <WeddingCaption>Annecy - SAVOIE</WeddingCaption>
          </WeddingCard>
        </LatestGrid>
        <CTAButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate(pathFor('portfolio', lang))}>
          {lang === 'fr' ? 'Voir le Portfolio' : 'See The Portfolio'}
        </CTAButton>
      </LatestWrap>

      
    </PageWrap>
  );
};

export default ExperiencePage;
